<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-start">
      <v-row class="flex-grow-1">
        <v-col cols="6" lg="4">
          <div style="display: flex; justify-content: space-between; width: 100%">
            <div class="mainTitle">
              {{ model.name | capitalize }}
              <!-- (SID: {{ model.sid }}) -->
              <div v-if="!isNewRecord && model.vipStatus">
                VIP Status: {{ model.vipStatus.isActive ? 'Active' : '-' }}
              </div>
            </div>
          </div>
          <div v-if="model.role" class="font-size-14 fullWidth">
            <div class="itemFieldName d-inline-block" style="min-width: 40px">Role:</div>
            {{ model.role | translateOptions(roleOptions) }}
          </div>
          <div v-if="model.user && model.user.username" class="font-size-14 fullWidth">
            <div class="itemFieldName d-inline-block" style="min-width: 40px">
              User Name (LoginID):
            </div>
            {{ model.user.username }}
          </div>
          <div class="font-size-14 fullWidth" v-if="model.team && model.team.label">
            <div class="itemFieldName d-inline-block" style="min-width: 40px">Team:</div>
            {{ model.team && model.team.label }}
          </div>
          <v-divider
            style="border-color: rgba(0, 0, 0, 0.1)"
            v-if="$vuetify.breakpoint.mdAndDown && isClient"
          />
        </v-col>
        <v-col class="d-flex" v-if="userId && !isNewRecord">
          <v-btn class="my-3 mx-3" min-height="56" @click="goToChangeUserNamePage"
            >Change User Name</v-btn
          >
          <v-btn class="my-3 mx-3" min-height="56" @click="goToChangePasswordPage"
            >Change Password &nbsp;
          </v-btn>
        </v-col>
      </v-row>
      <v-btn color="primary" @click="save" class="ml-3">Save</v-btn>
    </v-card-title>
    <!-- <v-divider class="mt-3" style="border-color: rgba(0, 0, 0, 0.1)" /> -->
    <v-card-text>
      <ValidationObserver ref="form">
        <v-form ref="form">
          <v-row>
            <!-- Photos Section (Combined Main Photo and Additional Photos) -->
            <v-col cols="12" v-if="isClient">
              <!-- <div class="mainTitle mb-4">Photos</div> -->

              <template v-if="!isNewRecord">
                <!-- Photo Grid with 6 slots (3:4 aspect ratio) -->
                <div class="d-flex flex-wrap">
                  <!-- Main Photo Slot (First slot) -->
                  <v-card
                    class="ma-2 photo-slot"
                    :elevation="image || (customerPhotos && customerPhotos[0]) ? 2 : 0"
                    outlined
                    width="150"
                    height="200"
                  >
                    <!-- If main photo exists -->
                    <div v-if="image" class="photo-container">
                      <v-img
                        :src="image"
                        height="200"
                        contain
                        @click="openImage"
                        style="cursor: pointer"
                      />
                      <div class="photo-overlay">
                        <v-chip small color="primary" class="ma-1">Main</v-chip>
                        <v-btn
                          icon
                          small
                          color="white"
                          @click.stop="removeImage"
                          class="delete-btn"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <!-- Empty main photo slot -->
                    <div v-else class="empty-slot" @click="$refs.mainPhotoInput.click()">
                      <v-icon large color="purple lighten-1">mdi-camera-plus</v-icon>
                      <div class="caption purple--text mt-2">Main Photo</div>
                      <div class="caption purple--text mt-2">(Profile avatar)</div>
                    </div>
                  </v-card>

                  <!-- Additional Photo Slots (5 more slots) -->
                  <div v-for="index in 5" :key="'photo-' + index">
                    <v-card
                      class="ma-2 photo-slot"
                      :elevation="customerPhotos[index - 1] ? 2 : 0"
                      outlined
                      width="150"
                      height="200"
                    >
                      <!-- If photo exists at this index -->
                      <div v-if="customerPhotos[index - 1]" class="photo-container">
                        <v-img
                          :src="attnThumbHref(customerPhotos[index - 1])"
                          height="200"
                          contain
                          @click="() => window.open(attnThumbHref(customerPhotos[index - 1]))"
                          style="cursor: pointer"
                        />
                        <div class="photo-overlay">
                          <v-btn
                            icon
                            small
                            color="white"
                            @click.stop="removePhoto(index - 1)"
                            class="delete-btn"
                          >
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </div>
                      <!-- Empty slot -->
                      <div
                        v-else
                        class="empty-slot"
                        @click="customerPhotos.length < 5 && $refs.additionalPhotosInput.click()"
                        :class="{ 'disabled-slot': customerPhotos.length >= 5 }"
                      >
                        <v-icon large color="purple lighten-1">mdi-plus</v-icon>
                        <div class="caption purple--text mt-2">Add Photo</div>
                      </div>
                    </v-card>
                  </div>
                </div>

                <!-- Hidden file inputs -->
                <input
                  ref="mainPhotoInput"
                  type="file"
                  accept="image/jpeg,image/png"
                  style="display: none"
                  @change="handleMainPhotoSelect"
                />
                <input
                  ref="additionalPhotosInput"
                  type="file"
                  accept="image/jpeg,image/png"
                  style="display: none"
                  @change="handleAdditionalPhotosSelect"
                />
              </template>
              <template v-else>
                <v-alert type="info" outlined dense>
                  Save the profile first to upload photos
                </v-alert>
              </template>
              <!-- <v-divider class="my-4" style="border-color: rgba(0, 0, 0, 0.1)" /> -->
            </v-col>

            <v-col cols="12">
              <v-row>
                <v-col cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="Name" rules="required">
                    <label-with-text-field
                      v-model="model.name"
                      :error="!!errors.length"
                      :error-messages="errors"
                      :required="true"
                      :rules="rules.nameRules"
                      item-field-name="Display Name"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" lg="4" class="d-flex align-center">
                  <div class="d-flex flex-column" style="width: 100%">
                    <!-- <div class="itemFieldName mb-2">Status</div> -->
                    <v-switch
                      v-model="model.enabled"
                      color="primary"
                      :label="model.enabled ? 'Active' : 'Inactive'"
                      inset
                      dense
                      hide-details
                      class="mt-0"
                    />
                  </div>
                </v-col>
                <!-- Password field for admin profiles during creation -->
                <v-col v-if="isNewRecord && isAdminProfile" cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="Password" rules="required">
                    <label-with-text-field
                      v-model="model.password"
                      :error="!!errors.length"
                      :error-messages="errors"
                      :required="true"
                      type="password"
                      autocomplete="new-password"
                      item-field-name="Password"
                    />
                  </ValidationProvider>
                </v-col>
                <!-- Username field for admin profiles during creation -->
                <v-col v-if="isNewRecord && isAdminProfile" cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="Username" rules="required">
                    <label-with-text-field
                      v-model="model.username"
                      :error="!!errors.length"
                      :error-messages="errors"
                      :required="true"
                      item-field-name="Username"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row>
                <!-- <v-col v-if="!isClient" cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="HKID">
                    <label-with-text-field
                      v-model="model.hkId"
                      :error="!!errors.length"
                      :error-messages="errors"
                      item-field-name="HKID"
                    />
                  </ValidationProvider>
                </v-col> -->
                <v-col v-if="isClient" cols="6" lg="4">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :rules="{ required: true, digits: 8 }"
                    name="Phone"
                  >
                    <label-with-text-field
                      v-model="phone"
                      :error="!!errors.length"
                      :error-messages="errors"
                      item-field-name="Phone"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" lg="4" v-if="isClient">
                  <label-with-text-field v-model="model.gender" item-field-name="Gender">
                    <v-select v-model="model.gender" :items="profileGenderOption" outlined />
                  </label-with-text-field>
                </v-col>

                <v-col v-if="isClient" cols="6" lg="4">
                  <label-with-text-field
                    v-model="model.locationDistrict"
                    item-field-name="District"
                  >
                    <v-select
                      v-model="model.locationDistrict"
                      :items="districtsOptions"
                      item-text="label"
                      item-value="value"
                      outlined
                    />
                  </label-with-text-field>
                </v-col>
              </v-row>
            </v-col>
            <!-- Additional Customer Profile Fields -->
            <v-col cols="12" v-if="isClient">
              <v-divider class="my-4" style="border-color: rgba(0, 0, 0, 0.1)" />
              <div class="mainTitle mb-4">Personal Information</div>
              <v-row>
                <v-col cols="6" lg="4">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="Birth Year"
                    :rules="{ min_value: 1900, max_value: 2099 }"
                  >
                    <label-with-text-field
                      v-model.number="customerBirthYear"
                      :error="!!errors.length"
                      :error-messages="errors"
                      :rules="rules.birthYearRules"
                      type="number"
                      item-field-name="Birth Year"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="Height">
                    <label-with-text-field
                      v-model.number="customerHeight"
                      :error="!!errors.length"
                      :error-messages="errors"
                      :rules="rules.heightRules"
                      type="number"
                      item-field-name="Height (cm)"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="Education">
                    <label-with-text-field
                      v-model="customerEducation"
                      :error="!!errors.length"
                      :error-messages="errors"
                      item-field-name="Education"
                    >
                      <v-select
                        v-model="customerEducation"
                        :items="educationOptions"
                        item-text="label"
                        item-value="value"
                        outlined
                      >
                        <template v-slot:selection="{ item }">
                          {{ item.emoji }} {{ item.label }}
                        </template>
                        <template v-slot:item="{ item }">
                          {{ item.emoji }} {{ item.label }}
                        </template>
                      </v-select>
                    </label-with-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" lg="4">
                  <ValidationProvider v-slot="{ errors }" name="Occupation">
                    <label-with-text-field
                      v-model="customerOccupation"
                      :error="!!errors.length"
                      :error-messages="errors"
                      item-field-name="Occupation"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field
                    v-model="customerCommunicationLanguage"
                    item-field-name="Communication Language"
                  >
                    <v-select
                      v-model="customerCommunicationLanguage"
                      :items="languageOptions"
                      item-text="label"
                      item-value="value"
                      outlined
                    >
                      <template v-slot:selection="{ item }">
                        {{ item.emoji }} {{ item.label }}
                      </template>
                      <template v-slot:item="{ item }">
                        {{ item.emoji }} {{ item.label }}
                      </template>
                    </v-select>
                  </label-with-text-field>
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field v-model="customerMbti" item-field-name="MBTI Type">
                    <v-select
                      v-model="customerMbti"
                      :items="tableManagementMBTIPreferenceOption"
                      outlined
                    />
                  </label-with-text-field>
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field
                    v-model="model.genderPreference"
                    item-field-name="Gender Preference"
                  >
                    <v-select
                      v-model="model.genderPreference"
                      :items="profileGenderPreferenceOption"
                      outlined
                    />
                  </label-with-text-field>
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field
                    v-model="customerRelationshipType"
                    item-field-name="Relationship Type"
                  >
                    <v-select
                      v-model="customerRelationshipType"
                      :items="relationshipTypeOptions"
                      item-text="label"
                      item-value="value"
                      outlined
                    >
                      <template v-slot:selection="{ item }">
                        {{ item.emoji }} {{ item.label }}
                      </template>
                      <template v-slot:item="{ item }">
                        {{ item.emoji }} {{ item.label }}
                      </template>
                    </v-select>
                  </label-with-text-field>
                </v-col>
              </v-row>
            </v-col>

            <!-- Personal Tags -->
            <v-col cols="12" v-if="isClient">
              <v-divider class="my-4" style="border-color: rgba(0, 0, 0, 0.1)" />
              <div class="mainTitle mb-4">Personal Tags</div>

              <!-- Display existing tags with v-chip -->
              <div class="d-flex flex-wrap align-center mb-3">
                <v-chip
                  v-for="(tag, index) in customerPersonalTags"
                  :key="index"
                  class="ma-1"
                  close
                  color="primary"
                  outlined
                  @click:close="removePersonalTag(tag)"
                >
                  <strong>{{ tag }}</strong>
                </v-chip>
              </div>

              <!-- Input field to add new tags -->
              <div class="d-flex align-center">
                <v-text-field
                  v-model="newPersonalTag"
                  outlined
                  dense
                  hide-details
                  placeholder="Add new personal tag"
                  @keyup.enter="addPersonalTag"
                  class="flex-grow-1 mr-3"
                />
                <v-btn
                  color="primary"
                  @click="addPersonalTag"
                  :disabled="!newPersonalTag || !newPersonalTag.trim()"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>
            </v-col>

            <!-- Interests -->
            <v-col cols="12" v-if="isClient">
              <v-divider class="my-4" style="border-color: rgba(0, 0, 0, 0.1)" />
              <div class="mainTitle mb-4">Favourite Activities</div>

              <!-- Display existing activities with v-chip -->
              <div class="d-flex flex-wrap align-center mb-3">
                <v-chip
                  v-for="(activity, index) in customerFavouriteActivities"
                  :key="index"
                  class="ma-1"
                  close
                  color="primary"
                  outlined
                  @click:close="removeFavouriteActivity(activity)"
                >
                  <strong>{{ getActivityLabel(activity) }}</strong>
                </v-chip>
              </div>

              <!-- Select field to add new activities -->
              <div class="d-flex align-center">
                <v-select
                  v-model="selectedActivity"
                  :items="availableActivityOptions"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  placeholder="Select an activity to add"
                  class="flex-grow-1"
                  @change="addFavouriteActivity"
                  clearable
                >
                  <template v-slot:item="{ item }"> {{ item.emoji }} {{ item.label }} </template>
                  <template v-slot:selection="{ item }">
                    {{ item.emoji }} {{ item.label }}
                  </template>
                </v-select>
              </div>
            </v-col>

            <!-- Favourite Date Activities -->
            <v-col cols="12" v-if="isClient">
              <v-divider class="my-4" style="border-color: rgba(0, 0, 0, 0.1)" />
              <div class="mainTitle mb-4">Favourite Date Activities</div>

              <!-- Display existing date activities with v-chip -->
              <div class="d-flex flex-wrap align-center mb-3">
                <v-chip
                  v-for="(activity, index) in customerFavouriteDateActivities"
                  :key="index"
                  class="ma-1"
                  close
                  color="primary"
                  outlined
                  @click:close="removeFavouriteDateActivity(activity)"
                >
                  <strong>{{ getDateActivityLabel(activity) }}</strong>
                </v-chip>
              </div>

              <!-- Select field to add new date activities -->
              <div class="d-flex align-center">
                <v-select
                  v-model="selectedDateActivity"
                  :items="availableDateActivityOptions"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  placeholder="Select a date activity to add"
                  class="flex-grow-1"
                  @change="addFavouriteDateActivity"
                  clearable
                >
                  <template v-slot:item="{ item }"> {{ item.emoji }} {{ item.label }} </template>
                  <template v-slot:selection="{ item }">
                    {{ item.emoji }} {{ item.label }}
                  </template>
                </v-select>
              </div>
            </v-col>

            <!-- Food Preferences -->
            <v-col cols="12" v-if="isClient">
              <v-divider class="my-4" style="border-color: rgba(0, 0, 0, 0.1)" />
              <div class="mainTitle mb-4">Food Preferences</div>

              <!-- Display existing food preferences with v-chip -->
              <div class="d-flex flex-wrap align-center mb-3">
                <v-chip
                  v-for="(food, index) in customerFoodPreferences"
                  :key="index"
                  class="ma-1"
                  close
                  color="primary"
                  outlined
                  @click:close="removeFoodPreference(food)"
                >
                  <strong>{{ getFoodLabel(food) }}</strong>
                </v-chip>
              </div>

              <!-- Select field to add new food preferences -->
              <div class="d-flex align-center">
                <v-select
                  v-model="selectedFood"
                  :items="availableFoodOptions"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  placeholder="Select a food preference to add"
                  class="flex-grow-1"
                  @change="addFoodPreference"
                  clearable
                >
                  <template v-slot:item="{ item }"> {{ item.emoji }} {{ item.label }} </template>
                  <template v-slot:selection="{ item }">
                    {{ item.emoji }} {{ item.label }}
                  </template>
                </v-select>
              </div>
            </v-col>

            <v-col cols="12" v-if="isTherapistProfile">
              <v-row>
                <v-col cols="6" lg="4">
                  <v-menu
                    v-model="menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <label-with-text-field item-field-name="Employment date">
                        <v-text-field
                          outlined
                          v-model="pickerDateEmployment"
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </label-with-text-field>
                    </template>
                    <v-date-picker v-model="pickerDateEmployment" />
                  </v-menu>
                </v-col>
                <v-col cols="6" lg="4">
                  <v-menu
                    v-model="menu1"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <label-with-text-field item-field-name="Termination date">
                        <v-text-field
                          outlined
                          v-model="pickerDateTermination"
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </label-with-text-field>
                    </template>
                    <v-date-picker v-model="pickerDateTermination" />
                  </v-menu>
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field item-field-name="Status">
                    <v-select
                      v-model="model.status"
                      :items="profileStatuses"
                      outlined
                      :disabled="!isAdmin"
                    />
                  </label-with-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" lg="4">
                  <label-with-text-field
                    v-model.number="model.servicingCapacity"
                    :disabled="!isAdmin"
                    type="number"
                    @keypress="restrictChars($event)"
                    item-field-name="Servicing Capacity"
                  />
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field
                    v-model.number="model.bnAutoAssignPriority"
                    :disabled="!isAdmin"
                    type="number"
                    @keypress="restrictChars($event)"
                    item-field-name="Auto Assign Priority"
                  />
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field
                    v-model.number="model.bnDisplayOrder"
                    :disabled="!isAdmin"
                    type="number"
                    @keypress="restrictChars($event)"
                    item-field-name="Timetable Display Order"
                  />
                </v-col>
                <v-col cols="6" lg="4">
                  <label-with-text-field v-model="model.remarks" item-field-name="Remarks" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </ValidationObserver>
    </v-card-text>
    <v-dialog
      v-model="confirmDialog"
      width="60%"
      max-width="600"
      @click:outside="
        () => {
          model.isBlocked = !model.isBlocked;
          confirmDialog = false;
        }
      "
    >
      <v-card>
        <div class="d-flex justify-space-around closeButton">
          <v-card-text class="blacklistText">{{ message }}</v-card-text>
          <v-icon
            @click="
              () => {
                model.isBlocked = !model.isBlocked;
                confirmDialog = false;
              }
            "
            >mdi-close
          </v-icon>
        </div>

        <v-card-actions class="d-flex justify-end">
          <!--                    <v-btn-->
          <!--                      @click="-->
          <!--                        () => {-->
          <!--                          model.isBlocked = !model.isBlocked;-->
          <!--                          confirmDialog = false;-->
          <!--                        }-->
          <!--                      "-->
          <!--                    >-->
          <!--                      Cancel-->
          <!--                    </v-btn>-->
          <v-btn @click="saveBlacklist()"> Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from '@/api';
import { deleteAttachment, readAttachment, uploadSingleAttachment } from '@/api/attachment';
import {
  patchProfile,
  patchProfileByMaster,
  patchProfileByCashier,
  ProfileStatus,
  updateClientLabels,
} from '@/api/auth/membership';
import { createLoginToken } from '@/api/auth/sso';
import { crudUpdate } from '@/api/crud';
import labelWithTextField from '@/components/customFormLabel/labelWithTextField';
import {
  districts,
  month,
  roleOptions,
  profileGenderOption,
  profileGenderPreferenceOption,
  districtsOptions,
  educationOptions,
  relationshipTypeOptions,
  languageOptions,
  foodOptions,
  activityOptions,
  tableManagementMBTIPreferenceOption,
  dateActivityOptions,
} from '@/data/optionsConstants';
import { attnThumbHref, attnDownloadHref, calendarDate, capitalize } from '@/services/filters';
import { getId, getOid, oidEquals } from '@/services/utils';
import { get, set } from 'lodash';
import moment from 'moment';
import QrcodeVue from 'qrcode.vue';
import { mapGetters } from 'vuex';
import customLabel from '@/components/customFormLabel/customLabel';
import RemarksDialog from '@/components/Identity/remarksDialog';
import { positionOption } from '@/data/optionsConstants';
import { pick } from 'lodash';

export default {
  components: {
    RemarksDialog,
    labelWithTextField,
    QrcodeVue,
    customLabel,
  },
  props: {
    model: {},
    userId: {},
    labels: {},
    isNewRecord: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      profileGenderOption,
      profileGenderPreferenceOption,
      districtsOptions,
      educationOptions,
      relationshipTypeOptions,
      languageOptions,
      foodOptions,
      activityOptions,
      dateActivityOptions,
      tableManagementMBTIPreferenceOption,
      menu: false,
      menu1: false,
      menu2: false,
      menu3: false,
      valid: false,
      checkbox: false,
      loginRequired: 0,
      showChangeTeam: false,
      dialog: false,
      temp: false,
      rules: {
        nameRules: [v => !!v || 'Name is required'],
        birthYearRules: [
          v => !v || (v >= 1900 && v <= 2099) || 'Birth year must be between 1900 and 2099',
        ],
        heightRules: [v => !v || (v >= 100 && v <= 250) || 'Height must be between 100 and 250 cm'],
      },
      photoUploadRules: [
        v => {
          const currentPhotos = this.customerPhotos?.length || 0;
          const newPhotosCount = v?.length || 0;
          return (
            currentPhotos + newPhotosCount <= 5 ||
            `Maximum 5 photos allowed. You currently have ${currentPhotos} photo(s).`
          );
        },
      ],
      link: {},
      qrcode: null,
      newImage: null,
      previousHardCopy: [],
      previousHardCopy1: [],
      newPhotos: [],
      confirmDialog: false,
      message: null,
      howToKnowBN: [
        'referred by',
        'Google',
        'RED 小紅書',
        'TikTok',
        'magazine',
        'facebook',
        'instagram',
        'website',
        'youtube',
        'other',
      ],
      month,
      remarksPopup: false,
      positionList: positionOption,
      newPersonalTag: '',
      selectedActivity: null,
      selectedDateActivity: null,
      selectedFood: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['role', 'isMaster', 'isAdmin', 'isCashier', 'user']),
    pickerDateEmployment: {
      get() {
        return this.model?.metadata?.employmentDate
          ? moment(this.model.metadata.employmentDate).format('YYYY-MM-DD')
          : null;
      },
      set(v) {
        this.$set(this.model.metadata, 'employmentDate', moment(v).toDate());
        this.menu = false;
      },
    },
    pickerDateTermination: {
      get() {
        return this.model?.metadata?.terminationDate
          ? moment(this.model.metadata.terminationDate).format('YYYY-MM-DD')
          : null;
      },
      set(v) {
        this.$set(this.model.metadata, 'terminationDate', moment(v).toDate());
        this.menu1 = false;
      },
    },
    pickerDate: {
      get() {
        return moment(this.model.created_at).format('YYYY-MM-DD');
      },
      set(v) {
        this.model.created_at = moment(v).toDate();
        this.menu2 = false;
      },
    },
    pickerDateBirthday: {
      get() {
        return this.model?.birthday ? moment(this.model.birthday).format('YYYY-MM-DD') : null;
      },
      set(v) {
        this.$set(this.model, 'birthday', moment(v).toDate());
        this.menu3 = false;
      },
    },
    calendarDate,
    districts: () => districts,
    roleOptions: () => roleOptions,
    image() {
      return this.imageId && attnThumbHref(this.imageId);
    },
    isClient() {
      return this.model.role === 'customer';
    },
    isTherapistProfile() {
      // do not mix up the use with 'isMaster' from mapGetters
      return this.model.role === 'bn.master';
    },
    isAdminProfile() {
      return this.model.role === 'admin';
    },
    isCashierProfile() {
      return this.model.role === 'bn.cashier';
    },
    id() {
      return this.model._id;
    },
    toggleHeart: {
      get() {
        return get(this.model, ['metadata', 'heart']);
      },
      set(v) {
        set(this.model, ['metadata', 'heart'], v);
      },
    },
    imageId() {
      return this.model.avatar;
    },
    phone: {
      get() {
        //return this.model.phone?.replace('+852', '');
        return this.model.phone;
      },
      set(v) {
        //this.$set(this.model, 'phone', v && `+852${v}`);
        this.$set(this.model, 'phone', v);
      },
    },

    phone2: {
      get() {
        return this.model.phone2;
      },
      set(v) {
        this.$set(this.model, 'phone2', v);
      },
    },
    MastersPosition: {
      get() {
        return get(this.model, ['metadata', 'position']);
      },
      set(v) {
        set(this.model, ['metadata', 'position'], v);
      },
    },
    EmergencyContactsName: {
      get() {
        return get(this.model, ['emergencyContacts', 'name']);
      },
      set(v) {
        set(this.model, ['emergencyContacts', 'name'], v);
      },
    },
    EmergencyContactsPhone: {
      get() {
        return get(this.model, ['emergencyContacts', 'phone']);
      },
      set(v) {
        set(this.model, ['emergencyContacts', 'phone'], v);
      },
    },
    EmergencyContactsRelationship: {
      get() {
        return get(this.model, ['emergencyContacts', 'relationship']);
      },
      set(v) {
        set(this.model, ['emergencyContacts', 'relationship'], v);
      },
    },
    userLabels: {
      get() {
        return get(this.model, 'metadata.labels', []);
      },
      set(v) {
        set(this.model, 'metadata.labels', v);
      },
    },
    profileStatuses() {
      return [
        { text: 'Active', value: ProfileStatus.accepted },
        { text: 'Inactive', value: ProfileStatus.rejected },
      ];
    },
    patchProfileByRole() {
      if (this.isMaster) {
        return patchProfileByMaster;
      } else if (this.isCashier) {
        return patchProfileByCashier;
      }
      return patchProfile;
    },
    crudProfilesPath() {
      return this.isMaster ? 'v1/master/profiles' : 'v1/profiles';
    },
    crudClientProfilesPath() {
      return this.isMaster ? 'v1/master/ClientProfiles' : 'v1/admin/ClientProfiles';
    },
    // New customer profile fields
    customerReferral: {
      get() {
        return get(this.model, 'referral', false);
      },
      set(v) {
        set(this.model, 'referral', v);
      },
    },
    customerLocation: {
      get() {
        return get(this.model, 'location', '');
      },
      set(v) {
        set(this.model, 'location', v);
      },
    },
    customerTags: {
      get() {
        return get(this.model, 'tags', []);
      },
      set(v) {
        set(this.model, 'tags', v);
      },
    },
    customerBirthYear: {
      get() {
        return get(this.model, 'birthYear');
      },
      set(v) {
        set(this.model, 'birthYear', v ? parseInt(v) : null);
      },
    },
    customerEducation: {
      get() {
        return get(this.model, 'education', '');
      },
      set(v) {
        set(this.model, 'education', v);
      },
    },
    customerHeight: {
      get() {
        return get(this.model, 'height');
      },
      set(v) {
        set(this.model, 'height', v ? parseFloat(v) : null);
      },
    },
    customerOccupation: {
      get() {
        return get(this.model, 'occupation', '');
      },
      set(v) {
        set(this.model, 'occupation', v);
      },
    },
    customerPersonalTags: {
      get() {
        return get(this.model, 'personalTags', []);
      },
      set(v) {
        set(this.model, 'personalTags', v);
      },
    },
    customerPhotos: {
      get() {
        return get(this.model, 'photos', []);
      },
      set(v) {
        set(this.model, 'photos', v);
      },
    },
    customerRelationshipType: {
      get() {
        return get(this.model, 'relationshipType', '');
      },
      set(v) {
        set(this.model, 'relationshipType', v);
      },
    },
    customerFavouriteActivities: {
      get() {
        return get(this.model, 'favouriteActivities', []);
      },
      set(v) {
        set(this.model, 'favouriteActivities', v);
      },
    },
    customerFavouriteDateActivities: {
      get() {
        return get(this.model, 'favouriteDateActivities', []);
      },
      set(v) {
        set(this.model, 'favouriteDateActivities', v);
      },
    },
    customerCommunicationLanguage: {
      get() {
        return get(this.model, 'communicationLanguage', '');
      },
      set(v) {
        set(this.model, 'communicationLanguage', v);
      },
    },
    customerMbti: {
      get() {
        return get(this.model, 'mbti', '');
      },
      set(v) {
        set(this.model, 'mbti', v);
      },
    },
    customerFoodPreferences: {
      get() {
        return get(this.model, 'foodPreferences', []);
      },
      set(v) {
        set(this.model, 'foodPreferences', v);
      },
    },
    availableActivityOptions() {
      // Filter out already selected activities
      return this.activityOptions.filter(
        activity => !this.customerFavouriteActivities.includes(activity.value),
      );
    },
    availableDateActivityOptions() {
      // Filter out already selected date activities
      return this.dateActivityOptions.filter(
        activity => !this.customerFavouriteDateActivities.includes(activity.value),
      );
    },
    availableFoodOptions() {
      // Filter out already selected food preferences
      return this.foodOptions.filter(food => !this.customerFoodPreferences.includes(food.value));
    },
  },
  watch: {
    model() {
      this.reload();
    },
  },
  methods: {
    openImage() {
      window.open(this.image);
    },
    moment,
    attnDownloadHref,
    attnThumbHref,
    get,
    async reload() {
      this.previousHardCopy = [];
      if (this.model?.previousHardCopy?.length) {
        this.previousHardCopy1 = await readAttachment(api, this.model.previousHardCopy, {
          returnArray: 1,
        });
      } else {
        this.previousHardCopy1 = [];
      }
    },
    async saveBlacklist() {
      try {
        await this.patchProfileByRole(api, this.model._id, { isBlocked: this.model.isBlocked });
        this.confirmDialog = false;
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Save Success',
          type: 'success',
          color: 'success',
        });
      } catch (err) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: err,
        });
      }
    },
    confirmBlacklist(v) {
      //if (this.isMaster) return;
      this.message = v
        ? capitalize(this.model.name) + ' will not be able to book online. Confirm to blacklist?'
        : capitalize(this.model.name) + ' will be able to book online. Confirm to unblacklist?';
      this.confirmDialog = true;
    },
    async createLoginToken() {
      try {
        const token = (await createLoginToken(api, this.id)).token;
        this.qrcode = location.origin + '/go' + '?token=' + token;
      } catch (err) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: err,
        });
      }
    },
    async updateHeart() {
      //if (this.isMaster) return;
      this.toggleHeart = !this.toggleHeart;
      await this.patchProfileByRole(api, this.id, { metadata: this.model.metadata });
    },
    async save() {
      try {
        if (!(await this.$refs.form?.validate?.())) {
          return;
        }

        if (this.isNewRecord) {
          // For new records, emit the save event to parent component
          this.$emit('save');
          return;
        }

        const patchData = pick(this.model, [
          'name',
          'phone',
          'gender',
          'genderPreference',
          'locationDistrict',
          'status',
          'role',
          'enabled',
          'avatar',
          // New customer fields
          'referral',
          'location',
          'tags',
          'birthYear',
          'education',
          'height',
          'occupation',
          'personalTags',
          'photos',
          'relationshipType',
          'favouriteActivities',
          'favouriteDateActivities',
          'communicationLanguage',
          'mbti',
          'foodPreferences',
        ]);

        await this.patchProfileByRole(api, this.id, patchData);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Saved',
          type: 'success',
          color: 'success',
        });
        this.$emit('reload');
      } catch (err) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: err,
        });
      }
    },
    async removeImage() {
      try {
        this.$set(this.model, 'avatar', null);
      } catch (e) {
        console.log(e);
      }
    },
    async saveImage() {
      try {
        const attachment = await uploadSingleAttachment(api, this.newImage, this.model._id);
        this.$set(this.model, 'avatar', attachment._id);
      } catch (e) {
        console.log(e);
      }
    },
    async uploadPreviousHardCopy() {
      if (!this.previousHardCopy.length) {
        return;
      }
      const attachments = await Promise.all(
        this.previousHardCopy.map(file => uploadSingleAttachment(api, file, this.model._id)),
      );
      await crudUpdate(api, this.crudProfilesPath, this.id, {
        previousHardCopy: this.model.previousHardCopy.concat(attachments).map(getId),
        role: this.model.role,
      });
      await this.$store.dispatch('alert/updateAlertMessage', {
        msg: 'Uploaded',
        type: 'success',
        color: 'success',
      });
      this.$emit('reload');
    },
    async removePreviousHardCopy(att) {
      await deleteAttachment(api, getId(att));
      await crudUpdate(api, this.crudProfilesPath, this.id, {
        previousHardCopy: this.model.previousHardCopy.filter(x => !oidEquals(att, x)),
        role: this.model.role,
      });
      await this.$store.dispatch('alert/updateAlertMessage', {
        msg: 'Deleted',
        type: 'success',
        color: 'success',
      });
      this.$emit('reload');
    },
    async patchMastersPosition(v) {
      try {
        const res = await crudUpdate(api, 'v1/admin/MasterProfiles', this.id, {
          'metadata.position': v,
        });
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Update Success',
          type: 'success',
          color: 'success',
        });
        this.model.metadata.position = res.metadata.position;
        this.$emit('reload');
      } catch (e) {}
    },
    async saveCustomerLabels(item) {
      try {
        await updateClientLabels(api, this.id, item);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Update Success',
          type: 'success',
          color: 'success',
        });
        this.userLabels = item;
        this.remarksPopup = false;
      } catch (e) {}
    },

    async goToChangePasswordPage() {
      if (!this.userId) return alert('cannot get user Id');
      if (getOid(this.user) === getOid(this.userId)) {
        this.$router.push({
          name: 'changeMyPassword',
        });
      } else {
        this.$router.push({
          name: 'changePassword',
          params: { id: this.userId },
        });
      }
    },

    async goToChangeUserNamePage() {
      if (!this.userId) return alert('cannot get user Id');
      this.$router.push({
        name: 'changeUserName',
        params: { id: this.userId },
      });
    },
    restrictChars($event) {
      if ($event.charCode === 0 || /\d/.test(String.fromCharCode($event.charCode))) {
        return true;
      } else {
        $event.preventDefault();
      }
    },
    // Methods for handling new customer fields
    removeTag(item) {
      const index = this.customerTags.indexOf(item);
      if (index >= 0) this.customerTags.splice(index, 1);
    },
    removePersonalTag(item) {
      const index = this.customerPersonalTags.indexOf(item);
      if (index >= 0) this.customerPersonalTags.splice(index, 1);
    },
    addPersonalTag() {
      if (this.newPersonalTag && this.newPersonalTag.trim()) {
        // Check if the tag doesn't already exist
        if (!this.customerPersonalTags.includes(this.newPersonalTag.trim())) {
          this.customerPersonalTags.push(this.newPersonalTag.trim());
        }
        // Clear the input field
        this.newPersonalTag = '';
      }
    },
    removeFavouriteActivity(activity) {
      const index = this.customerFavouriteActivities.indexOf(activity);
      if (index >= 0) {
        this.customerFavouriteActivities.splice(index, 1);
      }
    },
    addFavouriteActivity() {
      if (
        this.selectedActivity &&
        !this.customerFavouriteActivities.includes(this.selectedActivity)
      ) {
        this.customerFavouriteActivities.push(this.selectedActivity);
        this.selectedActivity = null; // Clear selection after adding
      }
    },
    getActivityLabel(value) {
      const activity = this.activityOptions.find(a => a.value === value);
      return activity ? `${activity.emoji} ${activity.label}` : value;
    },
    removeFavouriteDateActivity(activity) {
      const index = this.customerFavouriteDateActivities.indexOf(activity);
      if (index >= 0) {
        this.customerFavouriteDateActivities.splice(index, 1);
      }
    },
    addFavouriteDateActivity() {
      if (
        this.selectedDateActivity &&
        !this.customerFavouriteDateActivities.includes(this.selectedDateActivity)
      ) {
        this.customerFavouriteDateActivities.push(this.selectedDateActivity);
        this.selectedDateActivity = null; // Clear selection after adding
      }
    },
    getDateActivityLabel(value) {
      const activity = this.dateActivityOptions.find(a => a.value === value);
      return activity ? `${activity.emoji} ${activity.label}` : value;
    },
    removeFoodPreference(food) {
      const index = this.customerFoodPreferences.indexOf(food);
      if (index >= 0) {
        this.customerFoodPreferences.splice(index, 1);
      }
    },
    addFoodPreference() {
      if (this.selectedFood && !this.customerFoodPreferences.includes(this.selectedFood)) {
        this.customerFoodPreferences.push(this.selectedFood);
        this.selectedFood = null; // Clear selection after adding
      }
    },
    getFoodLabel(value) {
      const food = this.foodOptions.find(f => f.value === value);
      return food ? `${food.emoji} ${food.label}` : value;
    },
    async handleMainPhotoSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.newImage = file;
        await this.saveImage();
        event.target.value = ''; // Reset input
      }
    },
    async handleAdditionalPhotosSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length) {
        this.newPhotos = files;
        await this.uploadPhotos();
        event.target.value = ''; // Reset input
      }
    },
    async uploadPhotos() {
      if (!this.newPhotos.length) {
        return;
      }

      // Check if adding new photos would exceed the limit
      const currentPhotos = this.customerPhotos || [];
      if (currentPhotos.length >= 5) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Maximum 5 photos allowed. Please remove some photos before uploading new ones.',
          type: 'error',
          color: 'error',
        });
        return;
      }

      // Calculate how many photos can be uploaded
      const remainingSlots = 5 - currentPhotos.length;
      const photosToUpload = this.newPhotos.slice(0, remainingSlots);

      if (photosToUpload.length < this.newPhotos.length) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: `Only ${photosToUpload.length} photo(s) will be uploaded due to the 5 photo limit.`,
          type: 'warning',
          color: 'warning',
        });
      }

      try {
        const attachments = await Promise.all(
          photosToUpload.map(uploadSingleAttachment.bind(null, api)),
        );
        this.customerPhotos = currentPhotos.concat(attachments.map(getId));
        this.newPhotos = [];
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Photos uploaded successfully',
          type: 'success',
          color: 'success',
        });
      } catch (err) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: err,
        });
      }
    },
    async removePhoto(index) {
      try {
        this.customerPhotos.splice(index, 1);
      } catch (err) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: err,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.fullWidth {
  width: 100%;
}

.blacklistText {
  font-size: 20px;
  //padding-top: 15px !important;
  padding-bottom: 0 !important;
}

.closeButton {
  padding: 8px 16px;
}

.font-size-14 {
  font-size: 14px;
}

.placeholder {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.old-customer {
  margin-left: 7px;
}

.v-chip {
  padding-left: 12px !important;
  padding-right: 12px !important;
}

// Override v-chip close button position
::v-deep .v-chip--removable {
  position: relative !important;
  overflow: visible !important;
  margin-top: 6px !important;
  margin-right: 6px !important;

  .v-chip__close {
    position: absolute !important;
    top: -6px !important;
    right: -6px !important;
    height: 25px !important;
    width: 25px !important;
    margin: 0 !important;
    background-color: white !important;
    border-radius: 50% !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
    z-index: 1 !important;

    .v-icon {
      font-size: 14px !important;
    }

    &:hover {
      background-color: #f5f5f5 !important;
    }
  }

  .v-chip__content {
    padding-right: 12px !important;
  }
}

// Photo slot styles
.photo-slot {
  position: relative;
  overflow: hidden;

  .photo-container {
    position: relative;
    width: 100%;
    height: 100%;

    &:hover .photo-overlay {
      opacity: 1;
    }
  }

  .photo-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.5) 0%,
      transparent 30%,
      transparent 70%,
      rgba(0, 0, 0, 0.5) 100%
    );
    opacity: 0;
    transition: opacity 0.3s;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 8px;

    .delete-btn {
      background-color: rgba(255, 255, 255, 0.2);

      &:hover {
        background-color: rgba(244, 67, 54, 0.8);
      }
    }
  }

  .empty-slot {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background-color: #f3e5f5; // Light purple
    transition: background-color 0.3s;

    &:hover:not(.disabled-slot) {
      background-color: #e1bee7; // Darker purple on hover
    }

    &.disabled-slot {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
